/**
 * <video-scrubber>: clickable / draggable progress bar for a video.
 * Binds to the visible <video> inside the closest [data-video-scrubber-root].
 * Pointer seeking is handled here (iOS range inputs ignore taps on the track);
 * the inner range input provides keyboard + screen reader access.
 */

/** @param {number} seconds */
function formatTime(seconds) {
  const total = Math.max(0, Math.floor(seconds || 0));
  const minutes = Math.floor(total / 60);
  const secs = String(total % 60).padStart(2, '0');
  return `${minutes}:${secs}`;
}

/** @param {HTMLVideoElement} video */
function isVisible(video) {
  if (typeof video.checkVisibility === 'function') return video.checkVisibility();
  return video.offsetParent !== null;
}

class VideoScrubber extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {AbortController | undefined} */
  #videoController;
  /** @type {HTMLVideoElement | null} */
  #video = null;
  /** @type {HTMLInputElement | null} */
  #input = null;
  #frame = 0;
  #dragging = false;
  #resumeAfterDrag = false;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#input = this.querySelector('input[type="range"]');
    const root = this.closest('[data-video-scrubber-root]');
    if (!this.#input || !root) return;

    for (const type of ['mousedown', 'touchstart', 'click']) {
      this.addEventListener(type, (event) => event.stopPropagation(), { signal });
    }

    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.addEventListener('pointermove', this.#onPointerMove, { signal });
    this.addEventListener('pointerup', this.#onPointerUp, { signal });
    this.addEventListener('pointercancel', this.#onPointerUp, { signal });
    this.#input.addEventListener('input', this.#onInput, { signal });

    for (const video of root.querySelectorAll('video')) {
      video.addEventListener('play', () => this.#bindActiveVideo(), { signal });
      video.addEventListener('loadedmetadata', () => this.#bindActiveVideo(), { signal });
    }

    window.matchMedia('(min-width: 750px)').addEventListener('change', () => this.#bindActiveVideo(), { signal });

    this.#bindActiveVideo();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#videoController?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #bindActiveVideo() {
    const root = this.closest('[data-video-scrubber-root]');
    if (!root) return;

    const videos = /** @type {HTMLVideoElement[]} */ (Array.from(root.querySelectorAll('video')));
    const video = videos.find(isVisible) ?? videos[0] ?? null;

    if (video !== this.#video) {
      this.#videoController?.abort();
      this.#videoController = new AbortController();
      const { signal } = this.#videoController;
      this.#video = video;

      if (video) {
        video.addEventListener('timeupdate', () => this.#render(), { signal });
        video.addEventListener('durationchange', () => this.#render(), { signal });
        video.addEventListener('play', () => this.#startLoop(), { signal });
        video.addEventListener('pause', () => cancelAnimationFrame(this.#frame), { signal });
        video.addEventListener('emptied', () => this.#render(), { signal });
      }
    }

    this.#render();
    if (video && !video.paused) this.#startLoop();
  }

  #startLoop() {
    cancelAnimationFrame(this.#frame);
    const tick = () => {
      this.#render();
      if (this.#video && !this.#video.paused) this.#frame = requestAnimationFrame(tick);
    };
    this.#frame = requestAnimationFrame(tick);
  }

  #hasDuration() {
    const duration = this.#video?.duration;
    return Boolean(duration && Number.isFinite(duration));
  }

  #render() {
    const input = this.#input;
    const video = this.#video;
    if (!input) return;

    const ready = Boolean(video) && this.#hasDuration();
    input.disabled = !ready;
    this.toggleAttribute('data-disabled', !ready);

    if (!video || !ready) {
      this.style.setProperty('--video-scrubber-progress', '0');
      input.value = '0';
      return;
    }

    const ratio = Math.min(1, Math.max(0, video.currentTime / video.duration));
    this.style.setProperty('--video-scrubber-progress', String(ratio));
    input.value = String(Math.round(ratio * Number(input.max || 1000)));
    input.setAttribute('aria-valuetext', `${formatTime(video.currentTime)} / ${formatTime(video.duration)}`);
  }

  /** @param {number} ratio */
  #seek(ratio) {
    const video = this.#video;
    if (!video || !this.#hasDuration()) return;
    video.currentTime = Math.min(1, Math.max(0, ratio)) * video.duration;
    this.#render();
  }

  /** @param {PointerEvent} event */
  #ratioFromPointer(event) {
    const rect = this.getBoundingClientRect();
    if (!rect.width) return 0;
    const offset = event.clientX - rect.left;
    const ratio = offset / rect.width;
    return getComputedStyle(this).direction === 'rtl' ? 1 - ratio : ratio;
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    event.stopPropagation();
    if (event.button !== 0 || !this.#video || !this.#hasDuration()) return;

    event.preventDefault();
    this.#bindActiveVideo();
    this.#dragging = true;
    this.#resumeAfterDrag = !this.#video.paused;
    this.#video.pause();
    this.setAttribute('data-dragging', '');
    this.setPointerCapture(event.pointerId);
    this.#input?.focus({ preventScroll: true });
    this.#seek(this.#ratioFromPointer(event));
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (!this.#dragging) return;
    event.preventDefault();
    this.#seek(this.#ratioFromPointer(event));
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (!this.#dragging) return;
    this.#dragging = false;
    this.removeAttribute('data-dragging');
    if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
    if (this.#resumeAfterDrag) this.#video?.play().catch(() => {});
    this.#resumeAfterDrag = false;
  };

  #onInput = () => {
    const input = this.#input;
    if (!input) return;
    this.#seek(Number(input.value) / Number(input.max || 1000));
  };
}

if (!customElements.get('video-scrubber')) {
  customElements.define('video-scrubber', VideoScrubber);
}
